import React, { useEffect, useCallback, useState, useRef } from "react";
import "./style.scss";
import { useDispatch } from "react-redux";
import { store, useTypedSelector } from "redux/store";
import { useNavigate, useParams } from "react-router-dom";
import {
  QuestionPaperPreviewAsync,
  selectQuestionPaperPreviewDataList,
} from "redux/features/QuestionPaperListSlice";
import DOMPurify from "dompurify";
import { formatTiming } from "helpers/common";
import { Loader } from "components";
import { display } from "html2canvas/dist/types/css/property-descriptors/display";

// convert html tags in a proper way //
const stripPTags = (html) => {
  if (!html) return "";

  let temp = document.createElement("div");
  temp.innerHTML = html;

  return temp.textContent || temp.innerText || "";
};

// Option text length (strip HTML) for layout decision
const getOptionTextLength = (option: any) => {
  const raw = option?.data?.data ?? option?.data ?? "";
  const str = typeof raw === "string" ? raw : "";
  return str.replace(/<[^>]*>/g, "").trim().length;
};

const OPTION_LENGTH_THRESHOLD = 100; // above this, show options stacked (one per row)
const shouldUseStackedOptions = (questionOptions: any[] | undefined) => {
  if (!Array.isArray(questionOptions) || questionOptions.length === 0) return false;
  return questionOptions.some((opt) => getOptionTextLength(opt) > OPTION_LENGTH_THRESHOLD);
};

// Define A4 Page Size
const A4_HEIGHT = 1123;

// view Question paper component here //
const ViewQuestionPaperComponent = ({ setFinalPageCount }) => {

  const renderContent = useCallback((content = "") => {
    // Strip trailing <br> + optional whitespace/&nbsp; before closing </p> tags
    const cleaned = (content || "")
      .replace(/(<br\s*\/?>)\s*(&nbsp;|\u00a0)*\s*(<\/p>)/gi, "$3")
      .replace(/(<br\s*\/?>)\s*(<\/p>)/gi, "$2");
    const sanitizedBlog = DOMPurify.sanitize(cleaned);
    return <span className="html-content" dangerouslySetInnerHTML={{ __html: sanitizedBlog }}></span>;
  }, []);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  // Function to calculate the number of pages
  const calculatePages = useCallback(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    setTimeout(() => {
      const contentHeight = contentElement.scrollHeight;
      const estimatedPages = Math.ceil(contentHeight / A4_HEIGHT);
      setPageCount(estimatedPages || 1);

      // ✅ Ensure page count updates in PdfDownloadComponent before downloading
      if (setFinalPageCount) {
        setFinalPageCount(estimatedPages || 1);
      }
    }, 500);
  }, [setFinalPageCount]);

  useEffect(() => {
    if (!contentRef.current) return;

    const observer = new ResizeObserver(() => {
      calculatePages();
    });

    observer.observe(contentRef.current);

    return () => observer.disconnect();
  }, [calculatePages]);

  useEffect(() => {
    const images = document.querySelectorAll("img");
    images.forEach((img) => img.addEventListener("load", calculatePages));

    return () =>
      images.forEach((img) => img.removeEventListener("load", calculatePages));
  }, [calculatePages]);

  let dispatch = useDispatch<typeof store.dispatch>();
  const { id } = useParams();
  // List data here //
  const questionPaperData = useTypedSelector(
    selectQuestionPaperPreviewDataList
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      dispatch(QuestionPaperPreviewAsync({ id }));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (questionPaperData) {
      console.log("questionPaperData received:", questionPaperData);
      console.log("data_json:", questionPaperData.data_json);
      setTimeout(() => calculatePages(), 1000);
    }
  }, [questionPaperData, calculatePages]);

  if (!questionPaperData) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  const handleCancel = () => {
    navigate("/school-admin/question-papers");
  };

  // convert indexing in Letter like alphabet A , B, C //
  const indexToLetter = (index) => {
    return String.fromCharCode(65 + index);
  };

  // Helper function to find question or sub-question by ID
  const findQuestionById = (questions: any[] = [], id: number) => {
    console.log("findQuestionById - looking for id:", id);
    if (!Array.isArray(questions)) return undefined;

    // 1) Try direct questions
    let found = questions.find((q) => q?.id === id);
    if (found) {
      console.log("findQuestionById - found direct:", found);
      return found;
    }

    // 2) Try nested sub-questions
    for (const q of questions) {
      if (Array.isArray(q?.sub_questions)) {
        const sub = q.sub_questions.find((sq: any) => sq?.id === id);
        if (sub) {
          console.log("findQuestionById - found in sub_questions:", sub);
          return sub;
        }
      }
    }

    console.log("findQuestionById - not found");
    return undefined;
  };

  // Helper: find parent question (e.g., passage) that owns a given sub-question id
  const findParentQuestionForSubId = (questions: any[] = [], subId: number) => {
    if (!Array.isArray(questions)) return undefined;
    for (const q of questions) {
      if (Array.isArray(q?.sub_questions)) {
        const has = q.sub_questions.some((sq: any) => sq?.id === subId);
        if (has) return q;
      }
    }
    return undefined;
  };

  // Function to render a single question
  const renderQuestion = (
    question: any,
    questionNumber: number | string,
    isInORGroup: boolean = false,
    orLabel: string | null = null
  ) => {
    return (
      <div key={`question-${question.id}`} className="inner">
        {/* Show OR label if this is part of an OR group */}
        {isInORGroup && orLabel && (
          <div className="or-group-label">
            <strong>{orLabel}</strong>
          </div>
        )}

        <div className="question-area-left">
          {/* Question text with proper structure */}
          <div className="question-text-container" style={{display:"inline-flex"}} >
            <span className="question-number">{` Q${questionNumber}.`}</span>
            <span
              className="question-content html-content"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(question?.question_text || "-"),
              }}
            ></span>
          </div>

          {/* Options container aligned with question text */}
          <div className="options-container">
            <div className={`options-grid ${shouldUseStackedOptions(question?.question_options) ? "options-grid--stacked" : ""}`}>
              {question?.question_options?.map((option, optionIndex) => (
                <div key={optionIndex} className="option-item">
                  <span className="option-label">
                    {indexToLetter(optionIndex)}.
                  </span>
                  <span className="option-content">
                    {renderContent(option?.data?.data || option?.data)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Sub-questions if any */}
          {question?.sub_questions?.map((subQuestion, subIndex) => (
            <div key={subIndex} style={{ marginLeft: "calc(2.5ch + 5px)" }}>
              <span className="sub-question-number">{`(${String.fromCharCode(
                97 + subIndex
              )})`}</span>
              <span
                className="sub-question-content html-content"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(subQuestion?.question_text || "-"),
                }}
              ></span>
              <span className="sub-question-marks" style={{ float: "right" }}>
                [{subQuestion?.max_marks || 0} Mark]
              </span>
            </div>
          ))}
        </div>
        <div className="question-area-right">
          <p>[{question?.max_marks || question?.marks || 0} Mark]</p>
        </div>
      </div>
    );
  };

  // Accepts the full section to infer parent question for composite items
  const renderItems = (
    items: any[],
    questions: any[],
    questionNumberRef: any,
    section: any = null
  ) => {

    // Handle backward compatibility - if items doesn't exist or is empty,
    // fall back to rendering questions directly (old format)
    if (!items || items.length === 0) {
      console.log("No items, falling back to questions");
      return questions?.map((question) => {
        questionNumberRef.current += 1;
        return renderQuestion(question, questionNumberRef.current);
      });
    }

    // New format with items array
    return items?.map((item, itemIndex) => {
      console.log(`Processing item ${itemIndex}:`, item);

      if (item.nodeType === "question") {
        // Individual question
        const question = findQuestionById(questions, item.questionId);
        if (question) {
          questionNumberRef.current += 1;
          return renderQuestion(question, questionNumberRef.current);
        }
      } else if (item.nodeType === "composite") {
        // Composite group of sub-questions
        questionNumberRef.current += 1;
        const baseQuestionNumber = questionNumberRef.current;
        // Try to find the parent by sub-question relationship (more reliable)
        const firstSubId = item?.items?.[0]?.questionId;
        const parentQuestion = firstSubId
          ? findParentQuestionForSubId(questions, firstSubId)
          : undefined;

        return (
          <div key={`composite-group-${itemIndex}`} className="composite-group">
            {/* Render parent question text (e.g., passage) if available */}
            {parentQuestion?.question_text && (
              <div className="composite-parent-question">
                <div className="question-text-container" style={{display:"inline-flex"}}>
                  <span className="question-number">{`Q${baseQuestionNumber}.`}</span>
                  <span
                    className="question-content html-content"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        parentQuestion.question_text || "-"
                      ),
                    }}
                  ></span>
                </div>
              </div>
            )}
            {/* Composite Group Questions */}
            <div className="composite-group-questions">
              {item.items.map((compositeItem, compositeIndex) => {
                console.log("Processing composite item:", compositeItem);
                const question = findQuestionById(
                  questions,
                  compositeItem.questionId
                );
                if (question) {
                  const subQuestionLabel = `${baseQuestionNumber}(${indexToLetter(
                    compositeIndex
                  )})`;
                  return (
                    <div
                      key={`composite-question-${compositeItem.questionId}`}
                      className="composite-question"
                    >
                      {renderQuestion(question, subQuestionLabel, true)}
                    </div>
                  );
                }
                return null;
              })}
            </div>
            {/* Show total marks for the composite group */}
            {/* <div className="composite-total-marks">
              <span>[{item.totalMarks} Marks compo]</span>
            </div> */}
          </div>
        );
      } else if (item.nodeType === "group") {
        // OR group: multiple alternatives share the same question number
        questionNumberRef.current += 1;
        const baseQuestionNumber = questionNumberRef.current;

        return (
          <div key={`group-${itemIndex}`} className="or-group">
            {/* its only for showing instructions */}
            {/* {item?.displayLabel && (
              <div className="or-group-header">
                <strong>{item.displayLabel}</strong>
                {typeof item.pickCount === "number" && (
                  <span style={{ marginLeft: 8 }}>
                    (Pick {item.pickCount})
                  </span>
                )}
              </div>
            )} */}

            <div className="or-group-items">
              {item.items?.map((child, childIdx) => {
                const content = (() => {
                  if (child.nodeType === "question") {
                    const q = findQuestionById(questions, child.questionId);
                    if (!q) return null;
                    return (
                      <div key={`group-q-${childIdx}`} className="or-group-item">
                        {renderQuestion(q, baseQuestionNumber, true)}
                      </div>
                    );
                  }

                  if (child.nodeType === "composite") {
                    // Render composite inside OR group without incrementing number
                    const firstSubId = child?.items?.[0]?.questionId;
                    const parentQuestion = firstSubId
                      ? findParentQuestionForSubId(questions, firstSubId)
                      : undefined;

                    return (
                      <div key={`group-comp-${childIdx}`} className="or-group-item">
                        {parentQuestion?.question_text && (
                          <div className="composite-parent-question">
                            <div className="question-text-container" style={{display:"inline-flex"}}>
                              <strong className="question-number">{`Q${baseQuestionNumber}.`}</strong>
                              <span
                                className="question-content html-content"
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(
                                    parentQuestion.question_text || "-"
                                  ),
                                }}
                              ></span>
                            </div>
                          </div>
                        )}
                        <div className="composite-group-questions">
                          {child.items?.map((ci, cIdx) => {
                            const sq = findQuestionById(questions, ci.questionId);
                            if (!sq) return null;
                            const subQuestionLabel = `${baseQuestionNumber}(${indexToLetter(
                              cIdx
                            )})`;
                            return (
                              <div key={`group-comp-q-${ci.questionId}`} className="composite-question">
                                {renderQuestion(sq, subQuestionLabel, true)}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }

                  return null;
                })();

                if (!content) return null;

                return (
                  <React.Fragment key={`or-wrap-${childIdx}`}>
                    {childIdx > 0 && (
                      <div className="or-group-separator" style={{ textAlign: "center", margin: "8px 0" }}>
                        <strong>OR</strong>
                      </div>
                    )}
                    {content}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        );
      }
      return null;
    });
  };

  // Helper function to determine which data structure format to use
  const renderSectionContent = (section: any, questionNumberRef: any) => {
    // Check if this is the new format with items array
    if (section.items && Array.isArray(section.items)) {
      return renderItems(
        section.items,
        section.questions,
        questionNumberRef,
        section
      );
    }
    // Old format - render questions directly
    else if (section.questions && Array.isArray(section.questions)) {
      return section.questions.map((question) => {
        questionNumberRef.current += 1;
        return renderQuestion(question, questionNumberRef.current);
      });
    }
    return null;
  };

  // Helper function to group sections by letter only for display
  const groupSectionsByLetter = (dataJson: any[]) => {
    if (!Array.isArray(dataJson)) return {};
    
    const grouped: Record<string, any[]> = {};
    
    dataJson.forEach((section, index) => {
      const groupKey = `section_${section?.section}`;
      
      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }
      
      grouped[groupKey].push({
        ...section,
        originalIndex: index
      });
    });
    
    return grouped;
  };

  // Tsx here //
  return (
    <section className="question-paper">
      <button
        onClick={handleCancel}
        className="custom-inactive-button rounded-lg"
      >
        Cancel
      </button>
      <div className="container-fluid" ref={contentRef}>
        {questionPaperData ? (
          <div>
            <div className="top-area">
              <div className="left-area">
                <h6>
                  [Total No. of Questions: {questionPaperData?.total_questions}]
                </h6>
              </div>
            </div>
            <div className="subject-area">
              {questionPaperData?.header_1 && (
                <h4 className="subject-area-headers">
                  {questionPaperData?.header_1}
                </h4>
              )}
              {questionPaperData?.header_2 && (
                <h4 className="subject-area-headers">
                  {questionPaperData?.header_2}
                </h4>
              )}
              <h2 className="subject-area-headers">
                Class- {questionPaperData?.grade?.title}
              </h2>
              <h2 className="subject-area-headers">
                Subject- {questionPaperData?.subject?.title}
              </h2>
              <h4 className="subject-area-headers">
                {questionPaperData.type_test}
              </h4>
              {questionPaperData?.question_paper_created_type && (
                <h4>{questionPaperData?.question_paper_created_type}</h4>
              )}
            </div>
            <div className="exam-details-area">
              <p>{`[Duration: ${formatTiming(questionPaperData?.timing)}]`}</p>
              <p>{`[Max. Marks: ${questionPaperData?.question_paper_marks}]`}</p>
            </div>
            <div className="instruction-area">
              {/* <p>Instructions to the candidates:-</p> */}
              <ul>
                {questionPaperData?.instruction?.heading && (
                  <li>
                    <span>{questionPaperData.instruction.heading}</span>
                  </li>
                )}
                {questionPaperData?.instruction?.content ? (
                  questionPaperData?.instruction?.content
                    .split("\n")
                    .filter((instruction) => instruction.trim() !== "")
                    ?.map((instruction, index) => (
                      <li key={index}>
                        <span>{`${index + 1}. `}</span>{" "}
                        <span
                          dangerouslySetInnerHTML={{
                            __html: stripPTags(instruction),
                          }}
                        />
                      </li>
                    ))
                ) : (
                  <li>No Instructions</li>
                )}
              </ul>
            </div>

            <div className="question-area">
              {(() => {
                const groupedSections = groupSectionsByLetter(questionPaperData?.data_json || []);
                const questionNumberRef = { current: 0 };

                return Object.entries(groupedSections).map(([groupKey, sections]: [string, any[]]) => {
                  const firstSection = sections[0];
                  const sectionLetter = firstSection?.section;
                  
                  // Get unique subjects in this section
                  const uniqueSubjects = Array.from(
                    new Set(sections.map(s => s.subject_id).filter(Boolean))
                  );
                  
                  // Get subject titles
                  const subjectTitles = uniqueSubjects.map(subjectId => 
                    questionPaperData?.data_json
                      ?.find(s => s.subject_id === subjectId)
                      ?.subject?.title
                  ).filter(Boolean);

                  return (
                    <div key={groupKey} style={{ marginBottom: '20px' }}>
                      {/* Group Header - Show section letter */}
                      <h3 style={{ textAlign: "center" }}>
                        {`Section ${sectionLetter}`}
                        {subjectTitles.length > 0 && (
                          <span style={{ fontSize: '0.85em', color: '#0066cc', marginLeft: '10px' }}>
                            ({subjectTitles.join(', ')})
                          </span>
                        )}
                      </h3>

                      {/* Render each subsection (different subject/mark requirements) */}
                      {sections.map((section, subIndex) => (
                        <div key={`${groupKey}-${subIndex}`}>
                          {/* Show subsection indicator if multiple subsections exist */}
                          {sections.length > 1 && (
                            <div style={{ 
                              textAlign: 'center', 
                              fontSize: '0.9em', 
                              color: '#666',
                              fontStyle: 'italic',
                              marginTop: '10px',
                              marginBottom: '5px'
                            }}>
                              [{section?.max_marks} mark(s) each
                              {section?.subject?.title && ` - ${section.subject.title}`}]
                            </div>
                          )}
                          
                          {/* Section instruction */}
                          <div>
                            {stripPTags(section?.instruction) && (
                              <>
                                <strong className="grid place-items-center mt-1 mb-2">
                                  {stripPTags(section?.instruction)}
                                </strong>
                              </>
                            )}
                          </div>
                          
                          {/* Render section content */}
                          {renderSectionContent(section, questionNumberRef)}
                        </div>
                      ))}
                    </div>
                  );
                });
              })()}
            </div>

            {/* Footer with page information */}
            <div className="question-paper-footer">
              <div className="footer-content">
                <div className="footer-right">
                  <span>
                    Page {currentPage} of {pageCount}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <Loader />
          </div>
        )}
      </div>
    </section>
  );
};

export default ViewQuestionPaperComponent;
